import StService from './StService';
import { createApi } from '@stilingue/st-auth-utils';
import { isProdVersion } from '@stilingue/st-environment-controller-utils';
import secureBackendApiDomains from './secure-backend-api-domains';
import serviceVersion from './service-version';

const baseUrl = isProdVersion() ? secureBackendApiDomains.production : secureBackendApiDomains.staging;

const afterParser = response => response.data;
function getUniverseHistoryEntries(response) {
  if (response && response.history_entries) return response.history_entries;
  if (response && response.historyEntries) return response.historyEntries;

  return [];
}

function normalizePagination(p) {
  if (!p) return null;
  const get = (camel, snake) => (Object.prototype.hasOwnProperty.call(p, snake) ? p[snake] : p[camel]);

  const offset = get('offset', 'offset');
  const limit = get('limit', 'limit');
  const totalCount = get('totalCount', 'total_count');
  const returnedCount = get('returnedCount', 'returned_count');
  const hasMore = get('hasMore', 'has_more');

  return { offset, limit, totalCount, returnedCount, hasMore };
}

function normalizeUniverseHistoryResponse(response) {
  const historyEntries = getUniverseHistoryEntries(response);
  const rawPagination = response && response.pagination;

  const pagination = normalizePagination(rawPagination);

  return { historyEntries, pagination };
}

class PanelsService extends StService {
  constructor(apiBaseUrl, serviceVersion) {
    const api = createApi(`panels-service/${serviceVersion}/`, apiBaseUrl, { timeout: 120000 });
    super(api, { afterParser });
  }

  getPanelsByQuery(params = {}) {
    return this.api.get(
      { url: 'v2/panels/by-query', params: { field: params.field, values: params.values, projection: params.fields } },
    ).then(this.afterParser);
  }

  getPanelDetail({ accountId, universeId, fields }) {
    const fieldsParam = fields ? `?fields=${fields}` : '';
    return this.api.get({ url: `v2/panels/${accountId}/${universeId}${fieldsParam}` }).then(this.afterParser);
  }

  getGroupsByUniverse({ accountId, universeId }) {
    return this.getPanelDetail({ accountId, universeId, fields: 'groups' }).then(data => (data && data.groups) || []);
  }

  getPanelGroups({ accountId, universeId }) {
    const params = {
      from_datastore: true,
      fields: 'names',
      use_cache: false
    };

    return this.api.get({ url: `v2/panels/${accountId}/${universeId}`, params })
      .then(this.afterParser)
      .then(data => (data && data.names) || []);
  }

  addBrandGroup({ accountId, universeId, group, descriptors = '', lang = 'pt' }) {
    const params = {
      account_id: accountId,
      universe_id: universeId,
    };

    return this.api.post(
      {
        url: 'v2/groups/add-brand-group',
        group,
        descriptors,
        lang,
      },
      { params },
    ).then(this.afterParser);
  }

  patchBrandGroup({ accountId, universeId, id, title, newDescriptors, isBrand }) {
    const request = {
      url: `v2/groups/${accountId}/${universeId}/group`,
      id,
    };
    if (title !== undefined) request.title = title;
    if (newDescriptors !== undefined) request.new_descriptors = newDescriptors;
    if (isBrand !== undefined) request.is_brand = isBrand;

    return this.api.patch(request).then(this.afterParser);
  }

  patchCustomOption({ accountId, universeId, field, newValue }) {
    return this.api.patch({ url: `v2/panels/${accountId}/${universeId}`, field, new_value: newValue }).then(this.afterParser);
  }

  getPanelLimits({ accountId, universeId }) {
    return this.api.get({ url: `v2/panels/getuniverselimit/${accountId}/${universeId}` }).then(this.afterParser);
  }

  async getUniverseHistoryPage(universeId, { sinceDate = null, addActualState = true, offset = 0 } = {}) {
    const params = { add_actual_state: addActualState, offset };
    if (sinceDate) params.since_date = sinceDate;

    const response = await this.api.get(
      { url: `universe-history/${universeId}/history`, params },
    ).then(this.afterParser);

    return normalizeUniverseHistoryResponse(response);
  }
}

export default new PanelsService(baseUrl, serviceVersion);
